/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React from 'react'
import Text from '../../Text'
import Button from '../../Button'
import ProgressHeader from '../components/ProgressHeader'
import OptionCard from '../../OnboardingWizardModal/components/OptionCard'
import SingleDivisionIcon from '../icons/SingleDivisionIcon'
import MultipleDivisionsIcon from '../icons/MultipleDivisionsIcon'
import ConferenceDivisionsIcon from '../icons/ConferenceDivisionsIcon'
import { useCompetitionSetup } from '../context/CompetitionSetupContext'
import colors from '@sportninja/common/constants/appColors'
import { trackCompetitionStructure } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

/**
 * Structure Step component - Step 1 of the competition setup wizard
 * Allows users to choose between Single Division, Multiple Divisions, and Conference & Divisions
 */
const StructureStep: React.FC = () => {
  const { state, setStructureType, nextStep, skipWizard } =
    useCompetitionSetup()

  const handleStructureTypeSelect = (
    type: 'single' | 'multiple' | 'conference'
  ) => {
    console.log(`🔄 StructureStep: User selected ${type} structure`)

    // Track the structure choice
    const choice =
      type === 'single'
        ? 'single_division'
        : type === 'multiple'
        ? 'multi_division'
        : 'conference_divisions'
    trackCompetitionStructure(choice)

    setStructureType(type)
  }

  const handleNext = () => {
    if (state.structureType) {
      nextStep()
    }
  }

  return (
    <StepContainer>
      <ProgressHeader currentStep={1} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Choose Your Competition Structure
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Select how you want to organize teams in your competition
          </Text>
        </TitleSection>

        <OptionsContainer>
          <OptionCard
            id='single_division'
            icon={
              <SingleDivisionIcon
                color={
                  state.structureType === 'single'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_100
                }
                size={40}
              />
            }
            title='Single Division'
            description='All teams play in a single group or table'
            isSelected={state.structureType === 'single'}
            onClick={() => handleStructureTypeSelect('single')}
          />

          <OptionCard
            id='multiple_divisions'
            icon={
              <MultipleDivisionsIcon
                color={
                  state.structureType === 'multiple'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_100
                }
                size={40}
              />
            }
            title='Multiple Divisions'
            description='Organize teams into age groups or skill levels'
            isSelected={state.structureType === 'multiple'}
            onClick={() => handleStructureTypeSelect('multiple')}
          />

          <OptionCard
            id='conference_divisions'
            icon={
              <ConferenceDivisionsIcon
                color={
                  state.structureType === 'conference'
                    ? colors.PRIMARY
                    : colors.NEUTRAL_100
                }
                size={40}
              />
            }
            title='Conference & Divisions'
            description='Dealing with larger seasons'
            isSelected={state.structureType === 'conference'}
            onClick={() => handleStructureTypeSelect('conference')}
          />
        </OptionsContainer>

        <ActionsContainer>
          <Button
            text='NEXT'
            onClick={handleNext}
            variant='primary'
            uppercase
            disabled={!state.structureType}
            style={css`
              width: 100%;
            `}
          />
          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default StructureStep
